import { css } from '@emotion/react'
import {
  SCROLL_TO_RESULTADO_ID,
  ScrollableFieldsConfig,
  useConsultaSeguimentoAutoScroll,
} from '@mchat-rf/consulta-seguimento/hooks/useConsultaSeguimentoAutoScroll'
import { Heading, isEmpty, Text, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { RadioGroupBoxField } from 'components/form/field/RadioGroupBoxField'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { RefObject } from 'react'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoIndicatorBlock } from '../../components/ConsultaSeguimentoIndicatorBlock'
import { ConsultaSeguimentoIndicatorHelper } from '../../components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '../../components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '../../components/ConsultaSeguimentoPerguntaHeaderTitle'
import { ExemplosVerticalField, ExemplosVerticalFieldOptions } from '../../components/ExemplosVerticalField'
import { PerguntaSimNaoRadioListField } from '../../components/PerguntaSimNaoRadioListField'
import { isPerguntaStatusRespondendo } from '../../consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import { PassouFalhouEnum } from '../../enums-consultaSeguimento'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import {
  ConsultaSeguimentoPergunta12ExemplosEnum,
  ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum,
  ConsultaSeguimentoPergunta12Model,
} from './model-consultaSeguimentoPergunta12'
import {
  calculateResultadoPergunta12,
  conditionToCalculatePergunta12,
  getPergunta12ResultadoText,
  hasPergunta12ExemplosAtLeastTwoSim,
  hasPergunta12ExemplosFalhouAnySim,
  hasPergunta12ExemplosPassouAnySim,
  isPergunta12ExemplosAllFilled,
  isPergunta12ExemplosPassouFalhouAllFilled,
  isPergunta12ExemplosPassouFalhouAllNao,
} from './utils-consultaSeguimentoPergunta12'

interface ConsultaSeguimentoPergunta12FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta12Model>
  containerRef: RefObject<HTMLDivElement>
  goNextButtonRef: RefObject<HTMLButtonElement>
}

export function ConsultaSeguimentoPergunta12Field(props: Readonly<ConsultaSeguimentoPergunta12FieldProps>) {
  const { name, containerRef, goNextButtonRef } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta12Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta12Model>({
    name,
    conditionToCalculate: conditionToCalculatePergunta12,
    calculateResultado: calculateResultadoPergunta12,
  })

  const { getId, triggerScrollFor } = useConsultaSeguimentoAutoScroll<ConsultaSeguimentoPergunta12Model>({
    name,
    containerRef,
    goNextButtonRef,
    scrollConfig: PERGUNTA_12_SCROLL_CONFIG,
  })

  const isPerguntaHeaderSim = value.PERGUNTA_12_HEADER === SimNaoEnum.SIM
  const hasPerguntaExemplosPassouAnySim = hasPergunta12ExemplosPassouAnySim(value?.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)
  const hasPerguntaExemplosFalhouAnySim = hasPergunta12ExemplosFalhouAnySim(value?.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)
  const isExemplosAllFilledPassouFalhou = isPergunta12ExemplosPassouFalhouAllFilled(
    value?.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU
  )
  const isPerguntaExemplosPassouFalhouAllNao = isPergunta12ExemplosPassouFalhouAllNao(
    value?.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU
  )

  const resultadoText = getPergunta12ResultadoText(value)

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        {isPerguntaStatusRespondendo(value.status) && (
          <ConsultaSeguimentoPerguntaHeader
            pergunta={MChatPerguntaEnum.PERGUNTA_12}
            title={<ConsultaSeguimentoPerguntaHeaderTitle sufix=' fica muito incomodado com barulhos do dia a dia?' />}
            status={value.status}
            field={
              <SimNaoSwitchButtonField
                name={name.PERGUNTA_12_HEADER}
                onChange={() => triggerScrollFor('PERGUNTA_12_HEADER')}
              />
            }
          />
        )}
        {isPerguntaHeaderSim && (
          <VFlow>
            <VFlow vSpacing={0}>
              <ConsultaSeguimentoIndicatorStart />
            </VFlow>
            <VFlow vSpacing={0}>
              <div id={getId('PERGUNTA_12_EXEMPLOS')}>
                <PerguntaSimNaoRadioListField<ConsultaSeguimentoPergunta12ExemplosEnum>
                  name={name.PERGUNTA_12_EXEMPLOS}
                  nameOutro={name.OUTRO}
                  label='A criança tem uma reação negativa ao som de...'
                  description='Responda os exemplos até pontuar na Pergunta 12 ou avançar para o próximo fluxo'
                  options={PERGUNTA_12_EXEMPLOS_OPTIONS}
                  onChange={() => triggerScrollFor('PERGUNTA_12_EXEMPLOS')}
                  required
                />
              </div>
              {hasPergunta12ExemplosAtLeastTwoSim(value) && (
                <VFlow id={getId('PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU')}>
                  <VFlow vSpacing={0}>
                    <ConsultaSeguimentoIndicatorStep
                      text='Sim para dois ou mais:'
                      style={styles.exemplosIndicatorStep}
                    />
                    <ConsultaSeguimentoIndicatorHelper
                      title='Como ele reage a esses barulhos?'
                      subtitle='(Se o genitor não der um dos exemplos abaixo, perguntar cada um deles individualmente.)'
                    />
                  </VFlow>
                  <VFlow vSpacing={0}>
                    <ExemplosVerticalField<ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum>
                      name={name.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU}
                      label='Responda todos os exemplos, conforme as respostas do genitor'
                      options={PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU_OPTIONS}
                      title='A criança...'
                      onChange={() => triggerScrollFor('PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU')}
                      required
                    />
                    {isPerguntaExemplosPassouFalhouAllNao && <ConsultaSeguimentoIndicatorBlock />}
                    {isExemplosAllFilledPassouFalhou &&
                      hasPerguntaExemplosPassouAnySim &&
                      hasPerguntaExemplosFalhouAnySim && (
                        <VFlow vSpacing={0}>
                          <ConsultaSeguimentoIndicatorStep
                            text={
                              <VFlow vSpacing={0}>
                                <Text color='inherit'>Sim tanto para exemplos Passou quanto Falhou:</Text>
                                <Heading level={3} color='inherit'>
                                  Pergunta sobre a frequência
                                </Heading>
                              </VFlow>
                            }
                            style={styles.indicatorStep}
                          />
                          <div id={getId('PERGUNTA_12_FREQUENCIA')}>
                            <RadioGroupBoxField
                              name={name.PERGUNTA_12_FREQUENCIA}
                              label='Quais os que ele faz com mais frequência?'
                              options={PERGUNTA_12_FREQUENCIA_OPTIONS}
                              required
                              onChange={() => triggerScrollFor('PERGUNTA_12_FREQUENCIA')}
                            />
                          </div>
                        </VFlow>
                      )}
                  </VFlow>
                </VFlow>
              )}
            </VFlow>
          </VFlow>
        )}
      </VFlow>
      {value.resultado && <ConsultaSeguimentoIndicatorResultado text={resultadoText} resultado={value.resultado} />}
    </VFlow>
  )
}

const PERGUNTA_12_SCROLL_CONFIG: ScrollableFieldsConfig<ConsultaSeguimentoPergunta12Model> = {
  PERGUNTA_12_HEADER: {
    scrollConditions: [
      {
        if: (value) => value === SimNaoEnum.SIM,
        scrollTo: 'PERGUNTA_12_EXEMPLOS',
      },
      {
        if: (value) => value === SimNaoEnum.NAO,
        scrollTo: SCROLL_TO_RESULTADO_ID,
      },
    ],
  },
  PERGUNTA_12_EXEMPLOS: {
    scrollConditions: [
      {
        if: (_, allValues) => hasPergunta12ExemplosAtLeastTwoSim(allValues),
        scrollTo: 'PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU',
      },
      {
        if: (_, allValues) =>
          isPergunta12ExemplosAllFilled(allValues) && !hasPergunta12ExemplosAtLeastTwoSim(allValues),
        scrollTo: SCROLL_TO_RESULTADO_ID,
      },
    ],
    isList: true,
    dependsOn: 'PERGUNTA_12_HEADER',
  },
  PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU: {
    scrollConditions: [
      {
        if: (value) =>
          isPergunta12ExemplosPassouFalhouAllFilled(value) &&
          hasPergunta12ExemplosPassouAnySim(value) &&
          hasPergunta12ExemplosFalhouAnySim(value),
        scrollTo: 'PERGUNTA_12_FREQUENCIA',
      },
      {
        if: (value) =>
          isPergunta12ExemplosPassouFalhouAllFilled(value) &&
          hasPergunta12ExemplosPassouAnySim(value) &&
          !hasPergunta12ExemplosFalhouAnySim(value),
        scrollTo: SCROLL_TO_RESULTADO_ID,
      },
      {
        if: (value) =>
          isPergunta12ExemplosPassouFalhouAllFilled(value) &&
          hasPergunta12ExemplosFalhouAnySim(value) &&
          !hasPergunta12ExemplosPassouAnySim(value),
        scrollTo: SCROLL_TO_RESULTADO_ID,
      },
      {
        if: (value) => isPergunta12ExemplosPassouFalhouAllNao(value),
        scrollTo: 'BOTTOM',
      },
    ],
    dependsOn: 'PERGUNTA_12_EXEMPLOS',
  },
  PERGUNTA_12_FREQUENCIA: {
    scrollConditions: [
      {
        if: (value) => !isEmpty(value),
        scrollTo: SCROLL_TO_RESULTADO_ID,
      },
    ],
    dependsOn: 'PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU',
  },
}

const PERGUNTA_12_EXEMPLOS_OPTIONS: RadioOption<ConsultaSeguimentoPergunta12ExemplosEnum>[] = [
  {
    label: 'Máquina de lavar?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.MAQUINA_DE_LAVAR,
  },
  {
    label: 'Choro de bebês?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.CHORO_DE_BEBES,
  },
  {
    label: 'Aspirador de pó?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.ASPIRADOR_DE_PO,
  },
  {
    label: 'Secador de cabelo?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.SECADOR_DE_CABELO,
  },
  {
    label: 'Trânsito?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.TRANSITO,
  },
  {
    label: 'Grito de bebês?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.GRITO_DE_BEBES,
  },
  {
    label: 'Música alta?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.MUSICA_ALTA,
  },
  {
    label: 'Toque do telefone/campainha?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.TOQUE_TELEFONE_CAMPAINHA,
  },
  {
    label: 'Barulho de liquidificador?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.BARULHO_DE_LIQUIDIFICADOR,
  },
  {
    label: 'Lugares barulhentos como supermercado ou restaurante?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.LUGARES_BARULHENTOS,
  },
]

const PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU_OPTIONS: ExemplosVerticalFieldOptions<ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum> = {
  PASSOU: [
    {
      label: 'Tampa os ouvidos calmamente?',
      value: ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.TAMPA_OUVIDOS_CALMAMENTE,
    },
    {
      label: 'Diz a você que não gosta de barulho?',
      value: ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.NAO_GOSTA_BARULHO,
    },
  ],
  FALHOU: [
    {
      label: 'Grita?',
      value: ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.GRITA,
    },
    {
      label: 'Chora?',
      value: ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.CHORA,
    },
    {
      label: 'Tampa os ouvidos?',
      value: ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.TAMPA_OUVIDOS,
    },
  ],
}

const PERGUNTA_12_FREQUENCIA_OPTIONS: RadioOption<PassouFalhouEnum>[] = [
  {
    label: 'Exemplos PASSOU',
    value: PassouFalhouEnum.PASSOU,
  },
  {
    label: 'Exemplos FALHOU',
    value: PassouFalhouEnum.FALHOU,
  },
]

const styles = {
  indicatorStep: css`
    width: auto;
  `,
  exemplosIndicatorStep: css`
    width: auto;
  `,
}
